// The Storage pages read Volumes, Images and Secrets as the API serves them (resources.md §5.1 to §5.3,
// api/nodus/v1 volume_types.go, image_types.go and secret_types.go). These are the fields the pages show;
// everything else stays in View YAML.
import type { KObject } from "@nodus/api";
import { formatBytes } from "@nodus/ui";
import { CATALOG_PROJECT } from "../training/model";

export { CATALOG_PROJECT };

export type Scope = { org: string; project: string };

const BINARY: Record<string, number> = { Ki: 2 ** 10, Mi: 2 ** 20, Gi: 2 ** 30, Ti: 2 ** 40, Pi: 2 ** 50, Ei: 2 ** 60 };
const DECIMAL: Record<string, number> = { k: 1e3, M: 1e6, G: 1e9, T: 1e12, P: 1e15, E: 1e18 };

/** The bytes of a Kubernetes quantity ("50Gi", "200G", "175"); undefined for anything else. */
export function quantityBytes(q: string | undefined): number | undefined {
  const m = /^(\d+(?:\.\d+)?)(Ki|Mi|Gi|Ti|Pi|Ei|k|M|G|T|P|E)?$/.exec(q ?? "");
  if (!m) return undefined;
  return Number(m[1]) * (m[2] ? (BINARY[m[2]] ?? DECIMAL[m[2]] ?? 1) : 1);
}

/** "175.5 MiB" from a quantity; the quantity itself when it does not parse. */
export function sizeLabel(q: string | undefined): string {
  const bytes = quantityBytes(q);
  return bytes === undefined ? (q ?? "—") : formatBytes(bytes);
}

// Volumes

export type VolumeAccessMode = "ReadWriteOnce" | "ReadOnlyMany" | "ReadWriteMany";

export type VolumeSource = {
  upload?: Record<string, never>;
  huggingface?: { repo: string; revision?: string };
  git?: { repo: string; ref?: string };
  url?: { url: string };
  s3?: { uri: string; connection: string };
  connectionQuery?: { connection: string; format?: string };
};

export type VolumeSpec = {
  accessMode?: VolumeAccessMode;
  size?: string;
  source?: VolumeSource;
  commitInterval?: string;
  revisionHistoryLimit?: number;
  ttlAfterUnused?: string;
  maxCostUSD?: string;
};

export type VolumeStatus = {
  phase?: string;
  reason?: string;
  message?: string;
  revision?: number;
  used?: string;
  storage?: { bytes: number; billedBytes: number };
  holder?: { kind?: string; name?: string; attempt: string; epoch: number };
  readers?: number;
  import?: { bytes?: number; files?: number; startTime?: string; completionTime?: string };
  lastCommitTime?: string;
};

export type Volume = KObject<VolumeSpec, VolumeStatus>;

/** One row of `GET …/volumes/{name}/revisions`, newest first. */
export type VolumeRevision = { revision: number; time: string; size: number; files: number; origin: string; createdBy?: string };

export const ACCESS_MODES: { value: VolumeAccessMode; label: string; hint: string }[] = [
  { value: "ReadWriteOnce", label: "ReadWriteOnce", hint: "One writer at a time. The default for working data." },
  { value: "ReadOnlyMany", label: "ReadOnlyMany", hint: "Any number of readers; each sees the latest ready revision." },
  { value: "ReadWriteMany", label: "ReadWriteMany", hint: "Many writers; each commits its changed paths, last writer wins per path." },
];

/** Who has the Volume mounted: the writer holding the lease and the count of read-only mounts. */
export function usedBy(v: Volume): string | undefined {
  const h = v.status?.holder;
  const readers = v.status?.readers ?? 0;
  const parts = [
    h ? `${h.kind ? `${h.kind.toLowerCase()}/` : ""}${h.name ?? h.attempt}` : undefined,
    readers > 0 ? `${readers} ${readers === 1 ? "reader" : "readers"}` : undefined,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : undefined;
}

/** What filled the first revision, in words. */
export function sourceLabel(source: VolumeSource | undefined): string {
  if (!source) return "Empty";
  if (source.huggingface) return `Hugging Face ${source.huggingface.repo}`;
  if (source.git) return `Git ${source.git.repo}`;
  if (source.url) return `URL ${source.url.url}`;
  if (source.s3) return `S3 ${source.s3.uri}`;
  if (source.connectionQuery) return `Query through ${source.connectionQuery.connection}`;
  return "Upload";
}

export function importFix(v: Volume): string {
  return v.status?.message === "InsufficientCredits"
    ? "Add credits, then retry the import."
    : "Check the source, then retry the import.";
}

// Images

export type ImageStep = {
  aptInstall?: string[];
  pipInstall?: { packages: string[] };
  uvPipInstall?: { packages: string[] };
  uvSync?: { project: string; frozen?: boolean };
  run?: string[];
  copy?: { blob: string; dest: string };
  env?: Record<string, string>;
  workdir?: string;
};

export type ImageSpec = {
  base?: string;
  fromSandbox?: { name: string };
  python?: string;
  steps?: ImageStep[];
  dockerfile?: { inline?: string; blob?: string; context?: string; target?: string };
  platform?: string;
  tags?: string[];
};

export type ImageStatus = {
  phase?: string;
  reason?: string;
  message?: string;
  progress?: { stage: "Queued" | "Building" | "Pushing" };
  digest?: string;
  reference?: string;
  tags?: Record<string, string>;
  specHash?: string;
  size?: number;
  python?: string;
  build?: { attempt?: string; startTime?: string; completionTime?: string; cpuSeconds?: number; costUSD?: string; cached?: boolean };
};

export type Image = KObject<ImageSpec, ImageStatus>;

export type ImageSource = "Catalog" | "Snapshot" | "Dockerfile" | "Build" | "Base image";

/** Where an Image comes from: the nodus catalog project, a Sandbox snapshot, a Dockerfile, steps, or a bare base. */
export function imageSource(img: Image): ImageSource {
  if (img.metadata.namespace === CATALOG_PROJECT) return "Catalog";
  if (img.spec?.fromSandbox) return "Snapshot";
  if (img.spec?.dockerfile) return "Dockerfile";
  return img.spec?.steps?.length ? "Build" : "Base image";
}

/** The tags the Image publishes: the built ones once it has built, the requested ones before. */
export function imageTags(img: Image): string[] {
  const built = Object.keys(img.status?.tags ?? {});
  return built.length ? built : (img.spec?.tags ?? []);
}

/** `sha256:` and the first twelve hex digits. */
export function shortDigest(digest: string | undefined): string {
  return digest ? digest.slice(0, "sha256:".length + 12) : "—";
}

/** One build step as a line: what it does and to what. */
export function stepLabel(step: ImageStep): string {
  if (step.aptInstall) return `apt install ${step.aptInstall.join(" ")}`;
  if (step.pipInstall) return `pip install ${step.pipInstall.packages.join(" ")}`;
  if (step.uvPipInstall) return `uv pip install ${step.uvPipInstall.packages.join(" ")}`;
  if (step.uvSync) return `uv sync${step.uvSync.frozen ? " --frozen" : ""}`;
  if (step.run) return step.run.join(" && ");
  if (step.copy) return `copy ${step.copy.blob} to ${step.copy.dest}`;
  if (step.env) return `env ${Object.keys(step.env).join(" ")}`;
  if (step.workdir) return `workdir ${step.workdir}`;
  return "—";
}

export const IMAGE_FIXES: Record<string, string> = {
  BaseNotFound: "Check the base reference and, for a private registry, list its Registry Secret under imagePullSecrets. Then apply the Image again.",
  StepFailed: "Open the Build tab to see the step that failed, fix it, then apply the Image again.",
  BuildTimeout: "Split the build into smaller steps or move heavy installs into the base image, then apply the Image again.",
};

// Secrets

export type SecretType = "Opaque" | "Registry";

export type SecretStatus = {
  phase?: string;
  reason?: string;
  version?: number;
  keys?: string[];
  versions?: { version: number; createTime: string; createdBy?: string; keys?: string[] }[];
  lastUsedTime?: string;
};

export type Secret = KObject<{ type?: SecretType }, SecretStatus>;

/** When the current version was written, else when the Secret was created. */
export function secretUpdated(s: Secret): string | undefined {
  return s.status?.versions?.[0]?.createTime ?? s.metadata.creationTimestamp;
}

const SECRET_KEY = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
const SECRET_MAX_KEYS = 64;
const SECRET_MAX_VALUE_BYTES = 4 << 10;

/** The reason a key name is refused, the same rules the API applies; undefined when it is fine. */
export function keyProblem(key: string): string | undefined {
  if (!SECRET_KEY.test(key)) return "Start with a letter or underscore, then letters, digits and underscores (up to 128).";
  if (key.toUpperCase().startsWith("NODUS_")) return "Names starting with NODUS_ are reserved.";
  return undefined;
}

/** KEY=VALUE lines of a .env file, read the way `nodus secret create --from-env-file` reads them. */
export function parseEnvFile(text: string): { values: Record<string, string> } | { error: string } {
  const values: Record<string, string> = {};
  const lines = text.split("\n");
  for (const [i, raw] of lines.entries()) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const at = line.indexOf("=");
    if (at <= 0) return { error: `Line ${i + 1} is not KEY=VALUE.` };
    values[line.slice(0, at)] = line.slice(at + 1);
  }
  return { values };
}

/** Why a set of values cannot be written, or undefined. */
export function valuesProblem(values: Record<string, string>): string | undefined {
  const keys = Object.keys(values);
  if (!keys.length) return "Add at least one key.";
  if (keys.length > SECRET_MAX_KEYS) return `A Secret holds at most ${SECRET_MAX_KEYS} keys.`;
  const bad = keys.find((k) => keyProblem(k));
  if (bad) return `${bad}: ${keyProblem(bad)}`;
  const long = keys.find((k) => new TextEncoder().encode(values[k] ?? "").length > SECRET_MAX_VALUE_BYTES);
  return long ? `${long}: a value is at most 4 KiB.` : undefined;
}
